import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Eye, IndianRupee, MoreHorizontal, RotateCcw } from "lucide-react";
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AppButton, AppDataTable, FilterToolbar,
  RefundModal, StatusBadge,
} from "../../../ui";
import apiClient from "../../../../utils/apiClient";
import { DASH, formatCurrency, formatDate } from "../../../../utils/format";
import { TableHeading } from "../RecordCardHead";

const PAGE_SIZE = 10;

/** Money that came in and can still go back. */
const refundable = (p) => p.paymentStatus === "success" || p.paymentStatus === "partially_refunded";

async function downloadInvoice(fee) {
  const res = await apiClient.get(`/fees/${fee._id}/invoice`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoice-${fee._id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * PaymentHistory — every fee transaction on record, newest first.
 *
 * Props:
 *   studentId   — for the details route
 *   studentName — named in the refund dialog
 *   payments    — the `/fees/student/:id` resource
 *   courseNames — { [courseAssignmentId]: course name }
 *   onCollect   — omitted when the viewer may not collect
 *   onChanged   — after a refund, so balances and installments refresh too
 *   onNotify    — (message, type) for the page's toast
 */
const PaymentHistory = ({ studentId, studentName, payments, courseNames, onCollect, onChanged, onNotify }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [refundError, setRefundError] = useState("");

  const rows = useMemo(
    () => [...(payments.data || [])].sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)),
    [payments.data],
  );
  const received = rows.filter(refundable).reduce((s, p) => s + (Number(p.currentPayment) || 0) - (Number(p.refundAmount) || 0), 0);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const openDetails = (p) => navigate(`/students/${studentId}/fees/view/${p._id}`);

  const invoice = async (p) => {
    try {
      await downloadInvoice(p);
    } catch (err) {
      onNotify(err?.response?.data?.message || "Could not download the invoice.", "error");
    }
  };

  const refund = async (reason, amount) => {
    if (!refundTarget) return;
    setRefunding(true);
    setRefundError("");
    try {
      const endpoint = refundTarget.paymentGateway === "razorpay"
        ? `/payments/${refundTarget._id}/refund`
        : `/fees/${refundTarget._id}/refund`;
      await apiClient.post(endpoint, { reason, amount });
      setRefundTarget(null);
      onNotify("Refund recorded.", "success");
      onChanged();
    } catch (err) {
      setRefundError(err?.response?.data?.message || "Failed to refund this payment.");
    }
    setRefunding(false);
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      render: (p) => <span className="tw-tabular-nums">{formatDate(p.date || p.createdAt)}</span>,
    },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      render: (p) => (
        <span className="tw-font-semibold tw-tabular-nums tw-text-content">
          {formatCurrency(p.currentPayment)}
          {p.refundAmount > 0 && (
            <span className="tw-block tw-text-xs tw-font-normal tw-text-content-subtle">
              {formatCurrency(p.refundAmount)} refunded
            </span>
          )}
        </span>
      ),
    },
    {
      key: "mode",
      label: "Mode",
      render: (p) => (
        <span>
          {p.paymentMode || DASH}
          <span className="tw-block tw-text-xs tw-text-content-subtle">
            {p.paymentGateway === "razorpay" ? "Online" : "Recorded by staff"}
          </span>
        </span>
      ),
    },
    {
      key: "course",
      label: "Course",
      render: (p) => <span className="tw-text-content-muted">{courseNames[p.courseAssignmentId] || DASH}</span>,
    },
    {
      key: "reference",
      label: "Reference",
      render: (p) => {
        const ref = p.referenceNumber || p.razorpayPaymentId;
        return ref ? <span className="tw-font-mono tw-text-xs tw-text-content-muted">{ref}</span> : DASH;
      },
    },
    {
      key: "status",
      label: "Status",
      render: (p) => <StatusBadge status={p.paymentStatus || "success"} domain="feePayment" />,
    },
    {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      resizable: false,
      // The row opens the payment, so the menu must not let its click through.
      render: (p) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for the ${formatCurrency(p.currentPayment)} payment on ${formatDate(p.date || p.createdAt)}`} trigger={<MoreHorizontal size={16} />}>
            <ActionMenuItem onClick={() => openDetails(p)}><Eye size={14} /> View details</ActionMenuItem>
            {refundable(p) && (
              <ActionMenuItem onClick={() => invoice(p)}><Download size={14} /> Download invoice</ActionMenuItem>
            )}
            {refundable(p) && onCollect && (
              <>
                <ActionMenuDivider />
                <ActionMenuItem danger onClick={() => { setRefundError(""); setRefundTarget(p); }}>
                  <RotateCcw size={14} /> Refund
                </ActionMenuItem>
              </>
            )}
          </ActionMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <AppDataTable
        fill={false}
        toolbar={(
          <FilterToolbar
            flush
            actions={onCollect && (
              <AppButton size="sm" variant="outline" onClick={onCollect}>
                <IndianRupee size={16} /> Collect fee
              </AppButton>
            )}
          >
            <TableHeading
              title="Payments"
              count={rows.length ? `${rows.length} · ${formatCurrency(received)} received` : ""}
            />
          </FilterToolbar>
        )}
        columns={columns}
        rows={pageRows}
        rowKey={(p) => p._id}
        loading={payments.loading && !payments.data}
        onRowClick={openDetails}
        emptyMessage={payments.error ? "Could not load payments." : "No payments recorded yet."}
        pagination={{ page, totalPages, total: rows.length, pageSize: PAGE_SIZE, onChange: setPage, infoLabel: "payments" }}
      />

      <RefundModal
        show={!!refundTarget}
        onHide={() => { if (!refunding) { setRefundTarget(null); setRefundError(""); } }}
        onConfirm={refund}
        error={refundError}
        title="Refund payment"
        studentName={studentName}
        originalAmount={refundTarget?.currentPayment || 0}
        maxRefundable={(refundTarget?.currentPayment || 0) - (refundTarget?.refundAmount || 0)}
        isOnline={refundTarget?.paymentGateway === "razorpay"}
        loading={refunding}
      />
    </>
  );
};

export default PaymentHistory;
